/**
 * Coarse-pointer helpers for the Modern skin.
 *
 * The skin was drawn for a mouse: most controls sit between 19 px and 30 px
 * tall, which is comfortable with a cursor and a poor target for a fingertip.
 * Rather than enlarge them everywhere — the reading room is a dense information
 * display and desktop density is worth keeping — every rule built from this file
 * is scoped to touch input and leaves the pointer case exactly as it was.
 */

/**
 * Touch-input scope for every rule in this file.
 *
 * `pointer: coarse` describes the PRIMARY pointer, so this matches an iPad, a
 * phone and a Surface in tablet mode, and does not match a touchscreen laptop
 * being driven from its trackpad — which is the wanted split, since that user
 * has a fine pointer and desktop density suits them.
 *
 * `any-pointer: coarse` was the alternative and is deliberately not used: it is
 * true whenever a touchscreen merely EXISTS, so it would inflate the layout for
 * every touch-capable desktop regardless of how it is actually being driven.
 */
export const COARSE = "@media (pointer: coarse)";

/**
 * Minimum tappable edge, in px.
 *
 * 44 is Apple's HIG floor; Material asks for 48. 44 is the value used here
 * because several of these controls sit in a toolbar or a table row where 48
 * starts to cost a visible amount of vertical space, and the gain over 44 is
 * marginal.
 */
export const TOUCH_MIN = 44;

/**
 * Smallest font iOS Safari will focus without zooming the page.
 *
 * Below 16 px, focusing an input triggers an automatic viewport zoom that the
 * user then has to pinch back out of — on every field, every time. Applying
 * this under COARSE keeps the desktop type scale untouched.
 */
export const NO_ZOOM_FONT = 16;

/**
 * Grow a control to the touch minimum without growing what it contains.
 *
 * The box gets bigger; the icon or label inside keeps the size it was drawn at,
 * so the skin still reads as compact while the target stops being a coin toss.
 * Callers pass their own padding when the control is wider than it is tall.
 *
 * @param overrides extra declarations merged into the coarse-pointer rule
 */
export function touchTarget(overrides: {[key: string]: string | number} = {}) {
    return {
        minHeight: TOUCH_MIN,
        minWidth: TOUCH_MIN,
        // a min-height alone leaves the label glued to the top of a control
        // whose height came from line-height rather than from a set height
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        ...overrides,
    }
}

/**
 * Input/textarea treatment for touch: no zoom-on-focus, and a tappable height.
 *
 * `height: auto` is set alongside `minHeight` because several fields in the skin
 * carry a fixed pixel height (30, 33, 42) that would otherwise win.
 *
 * @param overrides extra declarations merged into the coarse-pointer rule
 */
export function touchField(overrides: {[key: string]: string | number} = {}) {
    return {
        fontSize: NO_ZOOM_FONT,
        height: "auto",
        minHeight: TOUCH_MIN,
        ...overrides,
    }
}
